import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AccordionList, AccordionListItem } from 'components/AccordionList';
import MilestoneProgress from 'components/MilestoneProgress';
import { Heading, Paragraph } from 'components/Typography';
import { ScreenerResultsCtx } from 'domains/signup/screener/Components/ScreenerResultsSummary/context';
import {
  splitQuestionAnswers,
  getQuestionTitle,
  getSortedMilestones,
  getTargetMilestoneText,
} from 'domains/signup/screener/data/questions';
import { TherapyTarget } from 'domains/signup/data/types';
import { Button } from 'components/Button';
import headerImg from 'assets/images/screener/questions-mascots.png';
import { ScreenerGradeBadge } from 'domains/signup/screener/Components/ProgressBadge';

export const ScreenerResultsQuestionsSection: React.FC = () => {
  const { results, onProceedToSchedule } = useContext(ScreenerResultsCtx);
  const { t } = useTranslation();

  const { questions, screenerAgeRangeTitle: screenerTitle, targetPatient } = results;

  return (
    <section id="answers">
      <img alt="" srcSet={headerImg} />
      <AccordionList
        title={t('screener.results.questions.title')}
        subTitle={t(`screener.results.questions.subTitle.${targetPatient}`)}
      >
        {questions.map(q => {
          const {
            question: {
              question: questionText,
              explanationText1,
              explanationText2,
              explanationTitle,
              possibleAnswers,
              targetMilestoneAnswer,
            },
            response,
            result,
          } = q;
          return (
            <AccordionListItem
              key={questionText}
              summary={
                <div>
                  {result === 'support-needed' && <ScreenerGradeBadge grade={result} className="mb-16" />}
                  <Heading level="h7" className="font-semibold">
                    {getQuestionTitle(explanationTitle, targetPatient as TherapyTarget, response, t)}
                  </Heading>
                </div>
              }
            >
              <div className="flex flex-col gap-40 items-start sm-d:items-stretch">
                <Paragraph gutterBottom={false}>{explanationText1}</Paragraph>
                {targetPatient === 'child' && (
                  <MilestoneProgress
                    milestones={getSortedMilestones(splitQuestionAnswers(possibleAnswers), targetMilestoneAnswer)}
                    currentMilestoneTitle={t('screener.results.questions.milestones.yourAnswer')}
                    currentMilestoneValue={response}
                    targetMilestoneTitle={getTargetMilestoneText(screenerTitle, t)}
                    targetMilestoneValue={targetMilestoneAnswer}
                  />
                )}
                <Paragraph gutterBottom={false}>
                  {result === 'support-needed'
                    ? explanationText2
                    : t(`screener.results.questions.metMilestoneSubtext.${targetPatient}`)}
                </Paragraph>

                <Button variant="secondary" size="lg" onClick={onProceedToSchedule}>
                  {t('screener.results.questions.scheduleButton')}
                </Button>
              </div>
            </AccordionListItem>
          );
        })}
      </AccordionList>
    </section>
  );
};
